import { getImageUrl, getOrganizationTypeProfileInfo } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import ProjectLinks from 'src/app/common/project.links';
import { getLocation } from 'src/app/components/map/shared/location.service';
import { getStatus } from 'src/app/components/profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { CustomMarkerData } from 'src/app/interfaces/map.interface';
import { CategoryBasicView, FullUser } from 'src/app/swagger-api';
import MapSettings from '../../map.settings';
import { NonAggregatedMarkerBuilder } from './non-aggregated-items-marker.builder';

export abstract class UserMarkerBuilder extends NonAggregatedMarkerBuilder<FullUser> {
  public getMarkerId(user: FullUser): string {
    return `${user.user_id}_${user.user_roles[0]}`;
  }

  protected abstract getSpecialization(user: FullUser): CategoryBasicView[];

  protected getCustomMarkerData(data: FullUser): CustomMarkerData {
    return this.getUserData(data);
  }

  protected getRole(user: FullUser): string {
    return `${AppSettings.USER_ROLE_PREFIX}${user.user_roles[0]}`;
  }

  protected getUserData(user: FullUser): CustomMarkerData {
    return {
      user: {
        userId: user.user_id,
        displayedName: user.displayed_name,
        status: getStatus(user),
        role: this.getRole(user),
        image: getImageUrl(user.image_url),
      },
      specialization: this.getSpecialization(user),
      displayedLocation: getLocation(user.profile.location),
      routerLink: `${ProjectLinks.LINK_PROFILE_VIEW}/${user.user_id}`,
    };
  }
}

export class OrganizationMarkerBuilder extends UserMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.ORGANIZATION_MARKER_ICON_URL;
  }

  protected getSpecialization(_: FullUser): CategoryBasicView[] {
    return [];
  }

  protected override getRole(user: FullUser): string {
    return getOrganizationTypeProfileInfo(user.organization.organization_type).value.toString();
  }

  protected override getUserData(user: FullUser): CustomMarkerData {
    return {
      ...super.getUserData(user),
      displayedLocation: getLocation(user.profile.location, {
        includeStreet: true,
      }),
    };
  }
}

export class PatientMarkerBuilder extends UserMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.PATIENT_MARKER_ICON_URL;
  }

  protected getSpecialization(user: FullUser): CategoryBasicView[] {
    return user.patient.diseases;
  }
}

export class DoctorMarkerBuilder extends UserMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.DOCTOR_MARKER_ICON_URL;
  }

  protected getSpecialization(user: FullUser): CategoryBasicView[] {
    return user.doctor.specialty;
  }
}

export class MedStaffMarkerBuilder extends UserMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.MED_STAFF_MARKER_ICON_URL;
  }

  protected getSpecialization(user: FullUser): CategoryBasicView[] {
    return user.med_staff.specialty;
  }
}

export class ResearcherMarkerBuilder extends UserMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.RESEARCHER_MARKER_ICON_URL;
  }

  protected getSpecialization(user: FullUser): CategoryBasicView[] {
    return user.researcher.specialty;
  }
}
